import { useState, useEffect, useRef } from 'react';
import { IconButton, CircularProgress } from '@mui/material';

import PropTypes from 'prop-types';

const CountdownIconButton = ({ onClick, size = 28, icon, duration = 5000 }) => {
    const [progress, setProgress] = useState(0);
    const [completed, setCompleted] = useState(false);
    const interval = 100;
    const step = 100 / (duration / interval);
    const onClickRef = useRef(onClick);
    const timerRef = useRef();

    useEffect(() => {
        onClickRef.current = onClick;
    }, [onClick]);

    useEffect(() => {
        setProgress(0);
        setCompleted(false);
        timerRef.current && clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setProgress((prev) => Math.min(prev + step, 100));
        }, interval);
        return () => timerRef.current && clearInterval(timerRef.current);
    }, [duration, step, interval]);

    useEffect(() => {
        if (progress >= 100 && !completed) {
            timerRef.current && clearInterval(timerRef.current);
            setCompleted(true);
            onClickRef.current?.();
        }
    }, [progress, completed]);

    const handleClick = () => {
        if (!completed) {
            timerRef.current && clearInterval(timerRef.current);
            setCompleted(true);
            setProgress(100);
            if (typeof onClickRef.current === 'function') {
                onClickRef.current();
            }
        }
    };

    return (
        <IconButton
            size="large"
            sx={{ position: 'relative', width: size, height: size }}
            disabled={completed}
            onClick={handleClick}
            aria-label="Countdown close"
        >
            <CircularProgress
                variant="determinate"
                value={progress}
                size={size}
                thickness={4}
                color="inherit"
                sx={{ position: 'absolute', top: 0, left: 0 }}
            />
            {icon}
        </IconButton>
    );
};

CountdownIconButton.propTypes = {
    onClick: PropTypes.func,
    size: PropTypes.number,
    icon: PropTypes.node.isRequired,
    duration: PropTypes.number,
};

export default CountdownIconButton;
